import React from 'react';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { JiraIcon } from '@atlaskit/logo';
import { useIsStagingOrDevOrLocal } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';

// Establishes a UFO hold if the page container suspended for some reason.
// This should only render non-null content during development. This makes it obvious there has been a top-level suspense when we didn't want one (e.g., in videos).
// In dev, the render looks like blank-page -> logo -> page skeleton -> page content. The logo would typically be rendered for under a second while the network call resolves.
// In prod, the render looks like blank-page -> blank-page -> page skeleton -> page content. End-users should be none the wiser that we were still preparing to render, or at least have fewer tiers of placeholder.
export const JiraPageContainerFallback = (props: { reason?: string }): React.JSX.Element => {
	const isNotProd = useIsStagingOrDevOrLocal();
	return (
		<>
			<UFOLoadHold name="jira-spa" />
			{isNotProd && (
				<Flex justifyContent="center" alignItems="center">
					<Box padding="space.1000">
						<JiraIcon
							label="Loading Jira..."
							testId="page-container-v2.common.ui.page-container.placeholder"
						/>
						<Text>
							<samp>(This logo and message only appears in non-production instances)</samp>
						</Text>
						<Text>
							<samp>
								The Jira page container suspended. This should not happen normally; something likely
								went wrong prefetching the <kbd>JiraSpaPageContainer</kbd> query. Current reason for
								rendering this placeholder is: {props.reason || 'unknown'}
							</samp>
						</Text>
					</Box>
				</Flex>
			)}
		</>
	);
};
